import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, ShoppingCart, Wrench, Cpu, Thermometer, Keyboard, Wind } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import CatalogSearchBar from '../components/CatalogSearchBar';
import PartProductCard from '../components/PartProductCard';
import ContactCTA from '../components/ContactCTA';
import HelpBlock from '../components/HelpBlock';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'pieces-detachees'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

const subCategories = [
  { id: 'pompes-massage', label: 'Pompes de massage', icon: Wind },
  { id: 'pompes-circulation', label: 'Pompes de circulation', icon: Wind },
  { id: 'rechauffeurs', label: 'Réchauffeurs', icon: Thermometer },
  { id: 'blowers', label: 'Blowers', icon: Wind },
  { id: 'cartes-electroniques', label: 'Cartes électroniques', icon: Cpu },
  { id: 'claviers', label: 'Claviers', icon: Keyboard },
];

const PiecesDetacheesPage = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchResults, setSearchResults] = useState(null); // null = no search active

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/parts`);
        // Filter for pieces detachees (excluding accessories, entretien, pompes a chaleur)
        const piecesDetachees = response.data.filter(p => 
          p.main_category === 'pieces-detachees' || 
          p.sub_category?.includes('pompe') ||
          p.sub_category?.includes('rechauffeur') ||
          p.sub_category?.includes('blower') ||
          p.sub_category?.includes('carte') ||
          p.sub_category?.includes('clavier')
        );
        setProducts(piecesDetachees);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = searchResults !== null 
    ? searchResults // If search is active, use search results
    : selectedCategory === 'all' 
      ? products 
      : products.filter(p => p.sub_category === selectedCategory);

  // Handle search results callback
  const handleSearchResults = (results) => {
    setSearchResults(results);
    // Reset category filter when searching
    if (results !== null) {
      setSelectedCategory('all');
    }
  };

  // JSON-LD for SEO - Enhanced
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Pièces détachées pour spa - Pompes, réchauffeurs, claviers",
    "description": "Catalogue complet de pièces détachées spa : pompe spa massage et circulation, réchauffeur spa, clavier Balboa et Gecko, carte électronique, blower. Compatible Balboa, Gecko, Waterway, LX. Livraison France.",
    "url": "https://atlantiquespaboutique.com/pieces-detachees-spa",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Pièces détachées spa",
      "description": "Pièces de rechange pour spa et jacuzzi",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Pompes de massage spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=pompes-massage" },
        { "@type": "ListItem", "position": 2, "name": "Pompes de circulation spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=pompes-circulation" },
        { "@type": "ListItem", "position": 3, "name": "Réchauffeurs spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=rechauffeurs" },
        { "@type": "ListItem", "position": 4, "name": "Blowers spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=blowers" },
        { "@type": "ListItem", "position": 5, "name": "Cartes électroniques spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=cartes-electroniques" },
        { "@type": "ListItem", "position": 6, "name": "Claviers spa Balboa Gecko", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa?category=claviers" }
      ]
    },
    "provider": {
      "@type": "Organization",
      "name": "Atlantique Spa Boutique",
      "url": "https://atlantiquespaboutique.com",
      "description": "Spécialiste pièces détachées spa en France - Balboa, Gecko, Waterway, LX Whirlpool"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://atlantiquespaboutique.com/" },
        { "@type": "ListItem", "position": 2, "name": "Pièces & Accessoires", "item": "https://atlantiquespaboutique.com/pieces-accessoires" },
        { "@type": "ListItem", "position": 3, "name": "Pièces détachées spa", "item": "https://atlantiquespaboutique.com/pieces-detachees-spa" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pieces-detachees-page">
      <SEO 
        title="Pièces détachées spa : pompe, réchauffeur, clavier Balboa | Atlantique Spa Boutique"
        description="Pièces détachées spa de qualité : pompe spa (massage, circulation), réchauffeur spa, clavier Balboa et Gecko, carte électronique spa, blower. Compatible grandes marques. Livraison France 48-72h."
        canonical="/pieces-detachees-spa"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/pieces-accessoires">Pièces & Accessoires</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Pièces détachées</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Header - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image="https://customer-assets.emergentagent.com/job_b3797b70-2366-4f7a-a978-30c05fd0fea5/artifacts/k6935kxj_BP501_601_Group_Product_Shots.png"
        imageAlt="Pièces détachées spa Balboa - Boîtiers de commande, claviers et contrôleurs"
        badge="Pièces de rechange"
        badgeIcon={Wrench}
        title="Pièces détachées pour spa"
        subtitle="Pièces compatibles sélectionnées pour leur fiabilité et disponibilité. Pompes, réchauffeurs, blowers, cartes électroniques, claviers... Livraison partout en France."
        overlayPosition="left"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* SEO Intro Text - Enhanced */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-3">Pièces détachées spa compatibles grandes marques</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            Votre spa a besoin d'une <strong>pièce de rechange</strong> ? Nous proposons une sélection de <strong>pièces détachées spa</strong> de qualité pour 
            assurer la réparation et le bon fonctionnement de votre équipement. <strong>Pompe spa</strong> (massage ou circulation), 
            <strong> réchauffeur spa</strong>, <strong>clavier spa</strong> Balboa ou Gecko, <strong>carte électronique spa</strong>, 
            <strong>blower</strong> – nous avons la pièce qu'il vous faut.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible Balboa</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible Gecko</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible Waterway</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible LX Whirlpool</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible Wellis</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Compatible Canadian Spa</span>
          </div>
        </div>

        {/* Unified Search Bar - Full Width Enhanced */}
        <div className="mb-6">
          <CatalogSearchBar 
            products={products}
            onSearchResults={handleSearchResults}
            placeholder="Rechercher une pièce ou une référence (ex : pompe LX WP250, clavier Balboa, filtre PRB50)"
            category="pieces-detachees"
            emptyIcon={Wrench}
            showHelpText={true}
          />
        </div>

        {/* Help Block - Moved below search */}
        <div className="mb-6">
          <HelpBlock />
        </div>

        {/* Search Results Indicator */}
        {searchResults !== null && (
          <div className="mb-6 flex items-center justify-between bg-[#1E3A5F]/5 rounded-lg px-4 py-3">
            <p className="text-slate-700">
              <span className="font-semibold">{filteredProducts.length}</span> résultat{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
            </p>
            <button 
              onClick={() => setSearchResults(null)}
              className="text-sm text-[#1E3A5F] font-medium hover:underline"
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {/* Category Filter - Hidden when search is active */}
        {searchResults === null && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Filtrer par type</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F]'
                }`}
              >
                Tous
              </button>
              {subCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    selectedCategory === cat.id
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F]'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-40 bg-slate-200 rounded-lg mb-4" />
                <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Wrench className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchResults !== null ? 'Aucun produit trouvé' : 'Aucune pièce dans cette catégorie'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchResults !== null 
                ? 'Essayez avec une autre référence, marque ou mot-clé.'
                : 'Nous ajoutons régulièrement de nouvelles références.'
              }
            </p>
            <Link to="/contact">
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]">
                Nous contacter pour une pièce spécifique
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredProducts.map((product) => (
              <PartProductCard 
                key={product.slug} 
                product={product}
                showCategoryBadge={selectedCategory === 'all' && searchResults === null}
              />
            ))}
          </div>
        )}

        {/* SEO Bottom Text - Enhanced */}
        <div className="mt-12 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Pourquoi choisir nos pièces détachées spa ?</h2>
          <div className="grid md:grid-cols-2 gap-6 text-slate-600 mb-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Pièces compatibles grandes marques</h3>
              <p className="text-sm leading-relaxed">
                Nos <strong>pièces détachées spa</strong> sont compatibles avec les principales marques : 
                <em> Balboa, Gecko, Waterway, LX Whirlpool, Wellis, Canadian Spa, Hydropool, Dimension One, Hotspring, Sundance</em>. 
                Bien vérifier les dimensions et références avant commande. 
                En cas de doute, <Link to="/contact" className="text-[#1E3A5F] hover:underline">contactez-nous</Link>.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Livraison France entière – offerte dès 80 € TTC</h3>
              <p className="text-sm leading-relaxed">
                Nous expédions vos <strong>pièces détachées spa</strong> partout en France métropolitaine sous 48-72h. 
                <strong> Pompe spa</strong>, <strong>réchauffeur</strong>, <strong>clavier Balboa</strong>, 
                <strong> carte électronique</strong> – tout est livré rapidement chez vous.
              </p>
            </div>
          </div>
          
          {/* Additional SEO content */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="font-semibold text-slate-900 mb-3">Les pannes de spa les plus fréquentes</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-600">
              <div>
                <h4 className="font-medium text-slate-800 mb-1">Spa ne chauffe plus</h4>
                <p>Souvent lié au <strong>réchauffeur spa</strong> ou à la carte électronique. Vérifier aussi la pompe de circulation.</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-800 mb-1">Erreur FLO / OH</h4>
                <p>Problème de circulation d'eau. Vérifier la <strong>pompe de circulation</strong>, le filtre ou les capteurs.</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-800 mb-1">Pompe bruyante</h4>
                <p>Roulement usé, <strong>pompe spa</strong> en fin de vie. Remplacement recommandé avant panne totale.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cross-links to other categories */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Link to="/accessoires-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Accessoires de spa</span>
              <p className="text-xs text-slate-500">Escaliers, couvertures...</p>
            </div>
          </Link>
          <Link to="/entretien-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Entretien de spa</span>
              <p className="text-xs text-slate-500">Filtres, produits...</p>
            </div>
          </Link>
          <Link to="/pompes-a-chaleur-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pompes à chaleur</span>
              <p className="text-xs text-slate-500">3 kW, 5 kW, 7 kW</p>
            </div>
          </Link>
        </div>

        {/* CTA pièce spécifique */}
        <ContactCTA category="Pièces détachées" />

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Vous ne trouvez pas votre pièce ?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Notre équipe peut vous aider à identifier la pièce compatible avec votre spa
          </p>
          <a href={`mailto:${EMAIL}`}>
            <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold gap-2">
              <Mail className="w-5 h-5" />
              Nous contacter
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default PiecesDetacheesPage;
